import type { KeyboardEvent } from 'react';

import { browserOnly } from '@farmersdog/utils';

import { getId } from './getId';
import type { AccordionRowItem } from './types';

export interface UseKeyboardNavigation {
  handleKeyDown: (index: number, e: KeyboardEvent<HTMLButtonElement>) => void;
}

/**
 * Create keydown handler for accordion navigation
 * https://www.w3.org/TR/wai-aria-practices-1.1/examples/accordion/accordion.html
 */
export const useKeyboardNavigation = (
  accordionId: string | undefined,
  rows: AccordionRowItem[]
): UseKeyboardNavigation => {
  const focusIndex = browserOnly((window: Window) => (index: number): void => {
    const focusId = getId('label', accordionId, index);
    if (!focusId) {
      return;
    }
    window.document.getElementById(focusId)?.focus();
  });

  const handleKeyDown = (
    index: number,
    e: KeyboardEvent<HTMLButtonElement>
  ): void => {
    if (e.defaultPrevented || !focusIndex) {
      return; // Do nothing if the event was already processed
    }

    switch (e.key) {
      case 'Down':
      case 'ArrowDown':
        focusIndex(rows[index + 1] ? index + 1 : 0);
        break;
      case 'Up':
      case 'ArrowUp':
        focusIndex(rows[index - 1] ? index - 1 : rows.length - 1);
        break;
      case 'Home':
        focusIndex(0);
        break;
      case 'End':
        focusIndex(rows.length - 1);
        break;
      default:
        return;
    }

    e.preventDefault();
  };

  return { handleKeyDown };
};
